import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { UserModel, UserTableFields } from '../models/vet-users/user.model';
import { Helper } from '../utils/helper/helper';

export class UserManagementDataSource extends DataSource<UserModel> {
  private cachedUsers: UserModel[];
  private readonly usersSubject$: BehaviorSubject<UserModel[]>;
  private readonly sortByKeySubject$: BehaviorSubject<'ASC' | 'DESC' | null> = new BehaviorSubject<'ASC' | 'DESC' | null>('DESC');
  private readonly subscription$ = new Subscription();

  public constructor(
    public users: UserModel[],
    public totalUsers: UserModel[]
  ) {
    super();

    this.cachedUsers = JSON.parse(JSON.stringify(users));
    this.usersSubject$ = new BehaviorSubject(
      this.cachedUsers.splice(0, 20)
    );
  }

  public connect(
    collectionView: CollectionViewer
  ): Observable<UserModel[]> {
    return this.usersSubject$.asObservable();
  }

  public disconnect(): void {
    this.usersSubject$.complete();
    this.sortByKeySubject$.complete();
    this.subscription$.unsubscribe();
  }

  public get users$(): Observable<UserModel[]> {
    return this.usersSubject$.asObservable();
  }

  private loadUsers(newData?: UserModel[]): void {
    if (newData) {
      this.cachedUsers = [...newData];
      this.usersSubject$.next(this.cachedUsers.splice(0, 20));
    }
  }

  public onScroll(): void {
    if (this.cachedUsers.length) {
      const currentUsers = this.usersSubject$.value;
      currentUsers.push(...this.cachedUsers.splice(0, 20));
      this.usersSubject$.next(currentUsers);
    }
  }

  public sort(sortBy: keyof UserModel): void {
    const currentOrder = this.sortByKeySubject$.value;
    this.sortByKeySubject$.next(currentOrder === 'ASC' ? 'DESC' : 'ASC');
    this.compareUsers(sortBy);
  }

  private compareUsers(keySorted: keyof UserModel): void {
    const users = [...this.users];

    users.sort((a, b) => 
    this.sortByKeySubject$.value === 'ASC'
      ?  keySorted === UserTableFields.USER_NAME
        ? this.sortStringWithEmptyValues('ASC',a,b,UserTableFields.USER_NAME)
        : keySorted === UserTableFields.FIRST_NAME
        ? this.sortStringWithEmptyValues('ASC',a,b,UserTableFields.FIRST_NAME)
        : keySorted === UserTableFields.EMAIL_ID
        ? this.sortStringWithEmptyValues('ASC',a,b,UserTableFields.EMAIL_ID)
        : keySorted === UserTableFields.PHONE_NUMBER
        ? this.sortStringWithEmptyValues('ASC',a,b,UserTableFields.PHONE_NUMBER)
        : this.sortValuesByName(a, b, keySorted)
      : keySorted === UserTableFields.USER_NAME
        ? this.sortStringWithEmptyValues('DESC', a, b, UserTableFields.USER_NAME)
        : keySorted === UserTableFields.FIRST_NAME 
        ? this.sortStringWithEmptyValues('DESC', a, b, UserTableFields.FIRST_NAME)
        : keySorted === UserTableFields.EMAIL_ID 
        ? this.sortStringWithEmptyValues('DESC', a, b, UserTableFields.EMAIL_ID)
        : keySorted === UserTableFields.PHONE_NUMBER 
        ? this.sortStringWithEmptyValues('DESC', a, b, UserTableFields.PHONE_NUMBER)
      : this.sortValuesByName(a, b, keySorted)
  );

    this.loadUsers(users);
  }

  private sortStringWithEmptyValues(
    order: string,
    a: UserModel,
    b: UserModel,
    fieldName: string
  ): any {

    console.log("sortStringWithEmptyValues", order, a, b, fieldName);
    const nameA = (a.username || '').toLowerCase();
    const nameB = (b.username || '').toLowerCase();

    if (fieldName === UserTableFields.FIRST_NAME) {
      const nameA = (a.firstName || '').toLowerCase();
      const nameB = (b.firstName || '').toLowerCase();
    }

    if (fieldName === UserTableFields.EMAIL_ID) {
      const nameA = (a.emailId || '').toLowerCase();
      const nameB = (b.emailId || '').toLowerCase();
    }

    if (fieldName === UserTableFields.PHONE_NUMBER) {
      const nameA = (a.phoneNumber || '').toLowerCase();
      const nameB = (b.phoneNumber || '').toLowerCase();
    }

    const comparison = nameA.localeCompare(nameB);

    return order === 'ASC' ? comparison : -comparison;
  }

  private sortValuesByName(
    a: UserModel,
    b: UserModel,
    keySorted: keyof UserModel
  ): any {
    if (a[keySorted] || b[keySorted]) {
      const aCompared = a[keySorted] as string;
      const bCompared = b[keySorted] as string;

      return this.sortByKeySubject$.value === 'ASC' && bCompared
        ? bCompared.localeCompare(
          aCompared,
          navigator.languages[0] || navigator.language,
          { numeric: true, ignorePunctuation: true }
        ) -
        aCompared.localeCompare(
          bCompared,
          navigator.languages[0] || navigator.language,
          { numeric: true, ignorePunctuation: true }
        )
        : aCompared.localeCompare(
          bCompared,
          navigator.languages[0] || navigator.language,
          { numeric: true, ignorePunctuation: true }
        ) -
        bCompared.localeCompare(
          aCompared,
          navigator.languages[0] || navigator.language,
          { numeric: true, ignorePunctuation: true }
        );
    }
  }

  public apply(users: UserModel[] = []): void {
    this.loadUsers(users);
  }

  public reset(users: UserModel[]): void {
    this.loadUsers(users);
  }
 
  public searchTable(term: string): any {
    term = Helper.escapeRegex(term.trim());
    if (term) {
      const searchResults = this.users.filter(user =>
        Object.keys(user).some(key =>
          user[key as keyof UserModel] && new RegExp(term, 'gi').test(String(user[key as keyof UserModel]))
        )
      );

      this.loadUsers(searchResults);
    } else {
      this.loadUsers(this.totalUsers);
    }
  }

}
